if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.galleryButtons = this.elements.viewer?.querySelectorAll('.slider-buttons button') || [];
        this.galleryButtons.forEach((button) => {
          button.addEventListener('click', this.onGalleryButtonClick.bind(this), true);
        });
        const activeMediaId = this.elements.viewer?.querySelector('[data-media-id].is-active')?.dataset.mediaId;
        if (activeMediaId) this.warmAdjacentMedia(activeMediaId);
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 120));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', () => {
            this.onThumbnailClick(mediaToSwitch.dataset.target);
          });
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.updateGalleryButtons();
      }

      async onGalleryButtonClick(event) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const direction = event.currentTarget.name === 'next' ? 1 : -1;
        if (!this.mql.matches) {
          this.navigateMobileGallery(direction);
          return;
        }

        if (this.isNavigating) return;

        const mediaIds = this.getNavigableMediaIds();
        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        const activeIndex = Math.max(mediaIds.indexOf(activeMedia?.dataset.mediaId), 0);
        const nextIndex = (activeIndex + direction + mediaIds.length) % mediaIds.length;
        if (!mediaIds[nextIndex]) return;

        this.isNavigating = true;
        try {
          await this.transitionToMedia(mediaIds[nextIndex], direction);
        } finally {
          this.isNavigating = false;
          this.updateGalleryButtons();
        }
      }

      onThumbnailClick(mediaId) {
        if (!this.mql.matches) {
          this.setActiveMedia(mediaId, false);
          return;
        }

        const mediaIds = this.getNavigableMediaIds();
        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        const activeIndex = mediaIds.indexOf(activeMedia?.dataset.mediaId);
        const nextIndex = mediaIds.indexOf(mediaId);
        if (nextIndex < 0 || nextIndex === activeIndex || this.isNavigating) return;

        this.isNavigating = true;
        this.transitionToMedia(mediaId, nextIndex > activeIndex ? 1 : -1).finally(() => {
          this.isNavigating = false;
          this.updateGalleryButtons();
        });
      }

      navigateMobileGallery(direction) {
        const slider = this.elements.viewer.slider;
        const mediaItems = Array.from(this.elements.viewer.sliderItems).filter((item) => item.clientWidth > 0);
        if (!slider || mediaItems.length < 2) return;

        const activeIndex = mediaItems.reduce((closestIndex, item, index) => {
          const currentDistance = Math.abs(item.offsetLeft - slider.scrollLeft);
          const closestDistance = Math.abs(mediaItems[closestIndex].offsetLeft - slider.scrollLeft);
          return currentDistance < closestDistance ? index : closestIndex;
        }, 0);
        const nextIndex = (activeIndex + direction + mediaItems.length) % mediaItems.length;
        this.prepareMedia(mediaItems[nextIndex]);
        slider.scrollTo({ left: mediaItems[nextIndex].offsetLeft, behavior: 'smooth' });
        this.warmAdjacentMedia(mediaItems[nextIndex].dataset.mediaId);
      }

      getNavigableMediaIds() {
        if (!this.elements.thumbnails) {
          return Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'), (item) => item.dataset.mediaId);
        }

        const visibleMediaIds = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'))
          .filter((thumbnail) => thumbnail.offsetParent !== null)
          .map((thumbnail) => thumbnail.dataset.target);
        return visibleMediaIds.length
          ? visibleMediaIds
          : Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'), (item) => item.dataset.mediaId);
      }

      warmAdjacentMedia(mediaId) {
        const mediaIds = this.getNavigableMediaIds();
        const activeIndex = mediaIds.indexOf(mediaId);
        if (activeIndex < 0 || mediaIds.length < 2) return;

        const neighbors = new Set([-1, 1].map((offset) => mediaIds[(activeIndex + offset + mediaIds.length) % mediaIds.length]));
        neighbors.forEach((neighborId) => {
          const item = this.elements.viewer.querySelector(`[data-media-id="${neighborId}"]`);
          this.prepareMedia(item);
        });
      }

      async transitionToMedia(mediaId, direction) {
        const previousMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        const nextMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!nextMedia || nextMedia === previousMedia) return;

        this.prepareMedia(nextMedia);
        if (!previousMedia || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.setActiveMedia(mediaId, false);
          return;
        }

        previousMedia.classList.add('b-gallery-slide--transitioning');
        nextMedia.classList.add('b-gallery-slide--transitioning');
        const distance = direction > 0 ? 100 : -100;
        const options = { duration: 240, easing: 'cubic-bezier(.22, .61, .36, 1)', fill: 'both' };
        const outgoing = previousMedia.animate(
          [{ transform: 'translateX(0)' }, { transform: `translateX(${-distance}%)` }],
          options
        );
        const incoming = nextMedia.animate(
          [{ transform: `translateX(${distance}%)` }, { transform: 'translateX(0)' }],
          options
        );

        try {
          await Promise.all([outgoing.finished, incoming.finished]);
          this.setActiveMedia(mediaId, false);
        } finally {
          outgoing.cancel();
          incoming.cancel();
          previousMedia.classList.remove('b-gallery-slide--transitioning');
          nextMedia.classList.remove('b-gallery-slide--transitioning');
        }
      }

      prepareMedia(mediaItem) {
        const image = mediaItem?.querySelector('img');
        if (!image || (image.complete && image.naturalWidth > 0)) return;

        image.loading = 'eager';
        if (typeof image.decode === 'function') image.decode().catch(() => undefined);
      }

      updateGalleryButtons() {
        if (!this.mql.matches || !this.galleryButtons.length) return;

        const mediaIds = this.getNavigableMediaIds();
        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        const activeIndex = mediaIds.indexOf(activeMedia?.dataset.mediaId);
        this.galleryButtons.forEach((button) => {
          button.toggleAttribute('disabled', mediaIds.length < 2);
        });
        const currentPage = this.elements.viewer.querySelector('.slider-counter--current');
        const totalPages = this.elements.viewer.querySelector('.slider-counter--total');
        if (currentPage) currentPage.textContent = Math.max(activeIndex + 1, 1);
        if (totalPages) totalPages.textContent = mediaIds.length;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.warmAdjacentMedia(activeMedia.dataset.mediaId);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.updateGalleryButtons();

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          this.scrollToActiveMedia(activeMedia);
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.mql.matches && this.dataset.desktopLayout.includes('thumbnail')) {
          const thumbnailList = this.elements.thumbnails.slider;
          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.offsetHeight;
          const visibleTop = thumbnailList.scrollTop;
          const visibleBottom = visibleTop + thumbnailList.clientHeight;
          if (thumbnailTop < visibleTop || thumbnailBottom > visibleBottom) {
            thumbnailList.scrollTo({ top: thumbnailTop, behavior: 'smooth' });
          }
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      // Bebeomi: scroll so the whole active image sits just below the sticky header.
      // Runs after the variant re-render settles and re-checks once, because focus
      // changes or touch momentum on phones can cancel a smooth scroll midway.
      headerOffset() {
        return Array.from(document.querySelectorAll('.shopify-section-group-header-group, sticky-header')).reduce(
          (max, el) => {
            const pos = getComputedStyle(el).position;
            return pos === 'sticky' || pos === 'fixed' ? Math.max(max, el.offsetHeight) : max;
          },
          0
        );
      }

      scrollToActiveMedia(activeMedia) {
        const run = (behavior) => {
          if (!activeMedia.isConnected) return;
          const offset = this.headerOffset();
          const rectTop = activeMedia.getBoundingClientRect().top;
          // Already fully visible below the header: nothing to do.
          if (rectTop >= offset - 1) return;
          const top = Math.max(0, Math.round(rectTop + window.scrollY - offset));
          window.scrollTo({ top, behavior });
        };
        clearTimeout(this.scrollTimer);
        clearTimeout(this.scrollCheckTimer);
        this.scrollTimer = setTimeout(() => {
          requestAnimationFrame(() => run('smooth'));
          this.scrollCheckTimer = setTimeout(() => run('auto'), 700);
        }, 60);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
